import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { BsBriefcase, BsBook } from "react-icons/bs";

gsap.registerPlugin(ScrollTrigger);

const ExperienceEducation = () => {
  useEffect(() => {
    gsap.delayedCall(1, () => {
      gsap.timeline({
        scrollTrigger: {
          trigger: "#exp-edu-section",
          start: "top 80%",
          toggleActions: "play none resume pause",
        },
      }).fromTo(
        "#section-heading",
        { y: 70, opacity: 0 },
        { y: 0, opacity: 1, ease: "power4.inOut", duration: 1.4 }
      );

      const expCards = document.querySelectorAll(".exp-card");
      expCards.forEach((card, index) => {
        gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none resume pause",
          },
        })
          .from(card, {
            x: index % 2 === 0 ? -100 : 100,
            opacity: 0,
            duration: 1,
            ease: "power4.out",
          })
          .from(
            card.querySelector(".exp-icon"),
            {
              scale: 0,
              rotation: 180,
              duration: 0.6,
              ease: "back.out(1.7)",
            },
            0.3
          );
      });

      const eduCards = document.querySelectorAll(".edu-card");
      eduCards.forEach((card, index) => {
        gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none resume pause",
          },
        }).from(card, {
          y: 50,
          opacity: 0,
          duration: 1,
          ease: "power4.out",
          delay: index * 0.2,
        });
      });
    });
  }, []);

const experiences = [
    {
      role: "Robotics Lab Trainer",
      company: "Umaga Edutech Pvt. Ltd.",
      location: "Khalilabad",
      period: "01 2026 – Present",
      description:
        "Conducting hands-on robotics, IoT, and Python programming training for students in Classes 3-12. Guiding STEM projects and competitions, maintaining lab equipment, and mentoring students for national and international level robotics/IoT challenges.",
      gradient: "from-violet-500 to-purple-500",
    },
    {
      role: "Robotics Innovation Engineer",
      company: "RoboNinja Academy LLP",
      location: "Gorakhpur",
      period: "07 2025 – 01 2026",
      description:
        "Delivered interactive robotics and STEM training to K–12 students, involving Arduino, sensors, and IoT. Set up classroom labs, facilitated hands-on learning, and applied embedded systems knowledge in real teaching environments.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      role: "Backend Intern",
      company: "GanakGyan Technologies",
      location: "Hybrid",
      period: "04 2025 – 07 2025",
      description:
        "Specialized in Spring Boot development. Implemented RESTful APIs to handle data operations, ensuring efficient communication between the Angular frontend and Spring Boot backend.",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      role: "UI-UX Intern",
      company: "Karyanest Solutions LLP",
      location: "Hybrid",
      period: "02 2025 – 04 2025",
      description:
        "Designed user-centric mobile app interfaces in Figma, then transitioned to React Native to implement pixel-perfect designs with seamless user experiences.",
      gradient: "from-purple-500 to-pink-500",
    },
  ];

  const education = [
    {
      degree: "B.Tech - CSE (Data Science)",
      institution: "Buddha Institute of Technology",
      location: "Gorakhpur, Uttar Pradesh",
      period: "12 2022 – 06 2026",
      score: "CGPA - 8.43 / 10",
      gradient: "from-orange-500 to-red-500",
    },
    {
      degree: "Intermediate (12th) - PCM",
      institution: "Mahatma Gandhi Inter College",
      location: "Gorakhpur, Uttar Pradesh",
      period: "04 2020 – 04 2022",
      score: "74.6%",
      gradient: "from-indigo-500 to-purple-500",
    },
  ];

  return (
    <div
      id="exp-edu-section"
      className="w-full py-20 xl:px-44 lg:px-36 md:px-32 sm:px-28 vvs:px-12 px-10"
    >
      {/* Section Heading */}
      <h1
        id="section-heading"
        className="text-white/70 font-Glimer-Outlined font-semibold lg:mb-20 mb-16 vvs:text-[4rem] sm:text-[5.2rem] md:text-[6.4rem] lg:text-[7.5rem] xl:text-[9.2rem] 2xl:text-[10.8rem] text-[3.5rem] leading-[3.5rem] sm:leading-[5.5rem] md:leading-[6rem] lg:leading-[7rem] xl:leading-[9rem] 2xl:leading-[10rem] cursor-default"
      >
        Journey & Growth
      </h1>

      {/* Experience Section */}
      <div className="mb-32">
        <div className="flex items-center mb-12 space-x-4">
          <BsBriefcase className="text-white h-8 w-8 md:h-10 md:w-10" />
          <h2 className="text-white font-Glimer-Bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
            Experience
          </h2>
        </div>

        <div className="space-y-8 lg:space-y-12">
          {experiences.map((exp, index) => (
            <div
              key={index}
              className="exp-card relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 lg:p-10 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-white/5"
            >
              {/* Gradient Icon */}
              <div
                className={`exp-icon absolute -top-6 -left-6 h-16 w-16 md:h-20 md:w-20 bg-gradient-to-br ${exp.gradient} rounded-full flex items-center justify-center shadow-lg`}
              >
                <BsBriefcase className="text-white h-8 w-8 md:h-10 md:w-10" />
              </div>

              {/* Content */}
              <div className="ml-12 md:ml-16">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <h3 className="text-white font-Glimer-Bold text-xl md:text-2xl lg:text-3xl mb-2">
                      {exp.role}
                    </h3>
                    <p className="text-white/80 font-Glimer-Medium text-lg md:text-xl">
                      {exp.company}
                    </p>
                  </div>
                  <div className="mt-2 md:mt-0 text-right">
                    <p className="text-white/60 font-Glimer-Light text-sm md:text-base">
                      {exp.period}
                    </p>
                    <p className="text-white/60 font-Glimer-Light text-sm md:text-base">
                      {exp.location}
                    </p>
                  </div>
                </div>
                <p className="text-white/70 font-Glimer-Light text-base md:text-lg leading-relaxed">
                  {exp.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Education Section */}
      <div>
        <div className="flex items-center mb-12 space-x-4">
          <BsBook className="text-white h-8 w-8 md:h-10 md:w-10" />
          <h2 className="text-white font-Glimer-Bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
            Education
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {education.map((edu, index) => (
            <div
              key={index}
              className="edu-card relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-white/5 overflow-hidden group"
            >
              {/* Gradient Background on Hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${edu.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
              ></div>

              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-white font-Glimer-Bold text-xl md:text-2xl mb-3">
                  {edu.degree}
                </h3>
                <p className="text-white/80 font-Glimer-Medium text-lg mb-2">
                  {edu.institution}
                </p>
                <p className="text-white/60 font-Glimer-Light text-sm mb-2">
                  {edu.location}
                </p>
                <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
                  <p className="text-white/60 font-Glimer-Light text-sm">
                    {edu.period}
                  </p>
                  <p
                    className={`font-Glimer-Bold text-lg bg-gradient-to-r ${edu.gradient} bg-clip-text text-transparent`}
                  >
                    {edu.score}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExperienceEducation;